// Pipeline page — interactive service-map flow-graph (spec §4, §5, §6, §10).
//
// Hosts the React Flow graph + the right-side NodeDrawer and runs TWO PARALLEL,
// INDEPENDENT fetches that merge onto the curated nodes (§3.3):
//   - invokeOps<PipelineSnapshot>('/pipeline')            — AWS infra + CloudWatch
//   - invokeAdmin<PipelineJobSummary>('admin_pipeline_jobs', {mode:'summary'})
//                                                          — Postgres "transactions"
// Each tier has its OWN error state: if the ops tier is down we still render the
// graph with job overlays, and vice-versa — one tier failing never blanks the page.
//
// The graph (React Flow + its bundle) is LAZY-LOADED (§4) so it doesn't weigh on
// other routes. The old three flat tables (rules / queues / lambdas) are preserved
// verbatim as a collapsible "Raw resources" <details> section — a rollback-safe
// fallback over the same working data (§10).
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { invokeOps, invokeAdmin, errMsg } from "../lib/adminApi";
import { getPipelineSnapshot } from "../lib/pipelineSnapshot";
import type { PipelineSnapshot, PipelineJobSummary } from "../lib/types";
import type { PipelineNode } from "../lib/pipelineTopology";
import { Section, Table, Td, Badge, Banner } from "../components/ui";
import NodeDrawer from "../components/pipeline/NodeDrawer";

// Lazy so React Flow's bundle is only pulled on /pipeline (§4).
const PipelineGraph = lazy(() => import("../components/pipeline/PipelineGraph"));

export default function PipelinePage() {
  // --- AWS-ops tier (infra snapshot) ---
  const [snapshot, setSnapshot] = useState<PipelineSnapshot | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState<string | null>(null);

  // --- Postgres job tier ("transactions" overlay) ---
  const [jobSummary, setJobSummary] = useState<PipelineJobSummary | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState<string | null>(null);

  // selected node → drawer
  const [selected, setSelected] = useState<PipelineNode | null>(null);

  // Freshness (ADM-10): when the ops snapshot was last captured + an opt-in
  // auto-refresh, so queue/DLQ depths don't silently age on the one screen an
  // operator leaves open during an incident.
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadOps = useCallback(async () => {
    setOpsLoading(true);
    setOpsError(null);
    try {
      // force:true — Pipeline is the live view; never serve it a stale cache
      // entry. It still PRIMES the shared cache for the Logs picker (ADM-3).
      const snap = await getPipelineSnapshot({ force: true });
      setSnapshot(snap);
      setFetchedAt(Date.now());
    } catch (e) {
      setOpsError(errMsg(e));
    } finally {
      setOpsLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobLoading(true);
    setJobError(null);
    try {
      const summary = await invokeAdmin<PipelineJobSummary>("admin_pipeline_jobs", {
        mode: "summary",
      });
      setJobSummary(summary);
    } catch (e) {
      setJobError(errMsg(e));
    } finally {
      setJobLoading(false);
    }
  }, []);

  // Fire both tiers in parallel; neither awaits the other (§3.3).
  const refresh = useCallback(() => {
    void loadOps();
    void loadJobs();
  }, [loadOps, loadJobs]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request
    refresh();
  }, [refresh]);

  // Opt-in 30s auto-refresh, mirroring the Logs pattern (ADM-10).
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(() => refresh(), 30_000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, refresh]);

  // Tick a clock so the "fetched Xs ago" label stays live between fetches.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  const loading = opsLoading || jobLoading;
  const ageSec = fetchedAt != null ? Math.max(0, Math.round((now - fetchedAt) / 1000)) : null;
  const freshness =
    ageSec == null
      ? null
      : ageSec < 60
        ? `fetched ${ageSec}s ago`
        : `fetched ${Math.round(ageSec / 60)}m ago`;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Pipeline</h1>
        <div className="flex items-center gap-3">
          {freshness && <span className="text-xs text-zinc-500">{freshness}</span>}
          <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-zinc-400">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={refresh}
            disabled={loading}
            className="rounded border border-zinc-700 px-3 py-1 text-sm hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      {snapshot?.region && (
        <p className="mt-1 text-xs text-zinc-500">region {snapshot.region}</p>
      )}

      {/* Per-tier error banners — either tier can fail without blanking the page. */}
      {opsError && (
        <Banner className="mt-2">
          Infra metrics unavailable (AWS ops): {opsError}
        </Banner>
      )}
      {jobError && (
        <Banner className="mt-2">
          Job overlay unavailable (admin_pipeline_jobs): {jobError}
        </Banner>
      )}

      {/* ===== Flow-graph + drawer ===== */}
      <div className="mt-3 flex gap-3">
        <div className="min-w-0 flex-1">
          <Suspense
            fallback={
              <div className="flex h-[70vh] w-full items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 text-sm text-zinc-500">
                Loading graph…
              </div>
            }
          >
            <PipelineGraph
              snapshot={snapshot}
              jobSummary={jobSummary}
              onNodeClick={setSelected}
            />
          </Suspense>
        </div>
        {selected && (
          <NodeDrawer
            node={selected}
            snapshot={snapshot}
            onClose={() => setSelected(null)}
            onChanged={loadOps}
          />
        )}
      </div>

      {/* ===== Raw resources (rollback-safe fallback, §10) ===== */}
      <details className="mt-6 rounded-lg border border-zinc-800">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-900">
          Raw resources
        </summary>
        <div className="px-3 pb-3">
          <RawResources snapshot={snapshot} onChanged={loadOps} />
        </div>
      </details>
    </div>
  );
}

// ===========================================================================
// RawResources — the old three flat tables, preserved verbatim as the
// rollback-safe fallback view (§10). Controls (rule toggle / DLQ retry) kept
// here too so the legacy view stays fully functional on its own.
// ===========================================================================
function RawResources({
  snapshot,
  onChanged,
}: {
  snapshot: PipelineSnapshot | null;
  onChanged: () => void;
}) {
  const [busyRule, setBusyRule] = useState<string | null>(null);
  const [dlqBusy, setDlqBusy] = useState<string | null>(null);
  const [dlqResults, setDlqResults] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function toggleRule(name: string, enabled: boolean) {
    setBusyRule(name);
    setError(null);
    try {
      await invokeOps("/pipeline/rule", {
        method: "POST",
        body: JSON.stringify({ name, enabled }),
      });
      onChanged();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyRule(null);
    }
  }

  async function retryDlq(dlqUrl: string) {
    setDlqBusy(dlqUrl);
    setDlqResults((prev) => ({ ...prev, [dlqUrl]: "" }));
    try {
      // Only the DLQ URL is sent — the ops Lambda derives the paired source queue
      // server-side (the *_dead → *_queue convention), so the client can't (and
      // doesn't need to) compute it.
      const result = await invokeOps<{ moved: number }>("/pipeline/dlq-retry", {
        method: "POST",
        body: JSON.stringify({ dlqUrl }),
      });
      setDlqResults((prev) => ({
        ...prev,
        [dlqUrl]: `Moved ${result.moved} message${result.moved === 1 ? "" : "s"}`,
      }));
      onChanged();
    } catch (e) {
      setDlqResults((prev) => ({
        ...prev,
        [dlqUrl]: `Error: ${errMsg(e)}`,
      }));
    } finally {
      setDlqBusy(null);
    }
  }

  return (
    <div>
      {error && <Banner className="mt-2">{error}</Banner>}

      <Section title="EventBridge schedules" err={snapshot?.errors.rules}>
        <Table head={["Rule", "State", "Schedule", ""]}>
          {snapshot?.rules.map((r) => (
            <tr key={r.name} className="border-t border-zinc-800">
              <Td mono>{r.name}</Td>
              <Td>
                <Badge ok={r.state === "ENABLED"}>{r.state}</Badge>
              </Td>
              <Td mono className="text-zinc-400">
                {r.schedule ?? "—"}
              </Td>
              <Td>
                <button
                  onClick={() => toggleRule(r.name, r.state !== "ENABLED")}
                  disabled={busyRule === r.name}
                  className="rounded border border-zinc-700 px-2 py-0.5 text-xs hover:bg-zinc-800 disabled:opacity-50"
                >
                  {busyRule === r.name ? "…" : r.state === "ENABLED" ? "Disable" : "Enable"}
                </button>
              </Td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section title="Queues" err={snapshot?.errors.queues}>
        <Table head={["Queue", "Messages", "In-flight", ""]}>
          {snapshot?.queues.map((q) => (
            <tr key={q.url} className="border-t border-zinc-800">
              <Td mono>{q.name}</Td>
              <Td>
                <span className={q.isDlq && q.messages > 0 ? "text-red-400" : ""}>
                  {q.messages}
                </span>
              </Td>
              <Td className="text-zinc-400">{q.inFlight}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  {q.isDlq && <Badge ok={q.messages === 0}>DLQ</Badge>}
                  {q.isDlq && (
                    <button
                      onClick={() => retryDlq(q.url)}
                      disabled={dlqBusy === q.url}
                      className="rounded border border-zinc-700 px-2 py-0.5 text-xs hover:bg-zinc-800 disabled:opacity-50"
                    >
                      {dlqBusy === q.url ? "…" : "Retry"}
                    </button>
                  )}
                  {dlqResults[q.url] && (
                    <span
                      className={`text-xs ${
                        dlqResults[q.url].startsWith("Error")
                          ? "text-red-400"
                          : "text-emerald-400"
                      }`}
                    >
                      {dlqResults[q.url]}
                    </span>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section title="Lambdas" err={snapshot?.errors.lambdas}>
        <Table head={["Function", "Runtime", "Last modified", "Mem", "Invocations", "Errors", "Avg duration"]}>
          {snapshot?.lambdas.map((l) => (
            <tr key={l.name} className="border-t border-zinc-800">
              <Td mono>{l.name}</Td>
              <Td className="text-zinc-400">{l.runtime ?? "—"}</Td>
              <Td className="text-zinc-400">{l.lastModified?.slice(0, 10) ?? "—"}</Td>
              <Td className="text-zinc-400">{l.memory ?? "—"}</Td>
              <Td className="text-zinc-400">{l.metrics?.invocations ?? "—"}</Td>
              <Td>
                {l.metrics == null && <span className="text-zinc-600">—</span>}
                {l.metrics != null && l.metrics.errors === 0 && (
                  <span className="text-zinc-500">0</span>
                )}
                {l.metrics != null && l.metrics.errors > 0 && (
                  <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-xs text-red-400">
                    {l.metrics.errors}
                  </span>
                )}
              </Td>
              <Td className="text-zinc-400">
                {l.metrics ? `${Math.round(l.metrics.duration)} ms` : "—"}
              </Td>
            </tr>
          ))}
        </Table>
      </Section>
    </div>
  );
}

// Presentational helpers (Section/Table/Td/Badge/Banner) live in ../components/ui.
